import { useEffect, useMemo, useRef, useState } from 'react';
import { useQueries } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  ClipboardList,
  DollarSign,
  Package,
  Search,
  ShoppingCart,
  Ticket,
  User,
  Zap,
  X,
} from 'lucide-react';
import type { TicketListItem } from '@/lib/api/tickets.js';
import { listTickets } from '@/lib/api/tickets.js';
import { listSales, type SaleListItem } from '@/lib/api/sales.js';
import { listCustomers } from '@/lib/api/customers.js';
import { listInventoryItems } from '@/lib/api/inventory.js';
import { searchRepairServices, type RepairServiceSearchHit } from '@/lib/api/repair-services.js';
import { formatCurrency } from '@/lib/format.js';
import {
  loadPaletteRecent,
  pushPaletteRecent,
  type PaletteRecentItem,
} from '@/lib/command-palette-recent.js';
import { cn } from '@/lib/utils.js';

function useDebouncedValue<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = window.setTimeout(() => setDebounced(value), delayMs);
    return () => window.clearTimeout(t);
  }, [value, delayMs]);
  return debounced;
}

type ResultKind = 'action' | 'nav' | 'ticket' | 'sale' | 'customer' | 'inventory' | 'price' | 'recent';

type PaletteResult = {
  id: string;
  kind: ResultKind;
  category: string;
  label: string;
  sublabel?: string;
  shortcut?: string;
  link?: string;
  action?: 'open_cash' | 'close_cash' | 'price_preview';
  priceHit?: RepairServiceSearchHit;
  icon?: 'zap' | 'ticket' | 'sale' | 'user' | 'package' | 'dollar' | 'clipboard';
};

function isPosSaleTicket(t: TicketListItem): boolean {
  return t.source === 'pos_sale' || t.ticketNumber.startsWith('SALE-');
}

function paletteSaleResult(sale: SaleListItem): PaletteResult {
  const customer = sale.customerName?.trim() || 'Walk-in';
  const number = sale.saleNumber || sale.ticketNumber || 'SALE';
  return {
    id: `sale-${sale.id}`,
    kind: 'sale',
    category: 'SALES',
    label: `${number} · ${customer}`,
    sublabel: [
      sale.itemSummary?.trim() || `${sale.itemCount} item${sale.itemCount === 1 ? '' : 's'}`,
      formatCurrency(sale.total),
    ]
      .filter(Boolean)
      .join(' · '),
    link: `/tickets/${sale.id}`,
    icon: 'sale',
  };
}

function paletteTicketResult(t: TicketListItem): PaletteResult {
  const customer = t.customerDisplayName ?? 'Walk-in';
  if (isPosSaleTicket(t)) {
    return paletteSaleResult({
      id: t.id,
      saleNumber: t.ticketNumber,
      ticketNumber: t.ticketNumber,
      customerName: t.customerDisplayName ?? null,
      itemCount: 0,
      itemSummary: t.lineItemSummary ?? t.repairSummary ?? t.deviceSummary ?? 'POS sale',
      subtotal: t.total ?? '0',
      total: t.total ?? '0',
      balanceDue: t.balanceDue ?? '0',
      status: t.status,
    });
  }
  return {
    id: `ticket-${t.id}`,
    kind: 'ticket',
    category: 'TICKETS',
    label: `${t.ticketNumber}${t.isWarranty ? ' · Warranty' : ''} · ${customer}`,
    sublabel: [t.isWarranty ? `Rework of ${t.parentTicketNumber ?? 'original ticket'}` : null, t.deviceSummary ?? t.repairSummary, t.status].filter(Boolean).join(' · '),
    link: `/tickets/${t.id}`,
    icon: 'ticket',
  };
}

const QUICK_ACTIONS: PaletteResult[] = [
  {
    id: 'action-quick-ticket',
    kind: 'action',
    category: 'QUICK ACTIONS',
    label: 'New Quick Ticket',
    shortcut: 'Ctrl+N',
    link: '/tickets/quick',
    icon: 'zap',
  },
  {
    id: 'action-pos',
    kind: 'action',
    category: 'QUICK ACTIONS',
    label: 'Open POS',
    shortcut: 'Ctrl+P',
    link: '/pos',
    icon: 'dollar',
  },
  {
    id: 'action-inquiry',
    kind: 'action',
    category: 'QUICK ACTIONS',
    label: 'New Inquiry',
    shortcut: 'Ctrl+I',
    link: '/inquiries/new',
    icon: 'clipboard',
  },
  {
    id: 'action-open-cash',
    kind: 'action',
    category: 'QUICK ACTIONS',
    label: 'Open Cash Session',
    action: 'open_cash',
    icon: 'dollar',
  },
  {
    id: 'action-close-cash',
    kind: 'action',
    category: 'QUICK ACTIONS',
    label: 'Close Cash Session',
    action: 'close_cash',
    icon: 'dollar',
  },
  {
    id: 'action-cash-report',
    kind: 'action',
    category: 'QUICK ACTIONS',
    label: 'View Cash Report',
    link: '/reports/end-of-day',
    icon: 'dollar',
  },
  {
    id: 'action-full-ticket',
    kind: 'action',
    category: 'QUICK ACTIONS',
    label: 'Create New Full Ticket',
    link: '/tickets/new',
    icon: 'ticket',
  },
];

const NAV_ITEMS: PaletteResult[] = [
  { id: 'nav-dashboard', kind: 'nav', category: 'NAVIGATE TO', label: 'Dashboard', link: '/dashboard' },
  { id: 'nav-tickets', kind: 'nav', category: 'NAVIGATE TO', label: 'Tickets', link: '/tickets' },
  { id: 'nav-customers', kind: 'nav', category: 'NAVIGATE TO', label: 'Customers', link: '/customers' },
  { id: 'nav-inventory', kind: 'nav', category: 'NAVIGATE TO', label: 'Inventory', link: '/inventory' },
  { id: 'nav-messages', kind: 'nav', category: 'NAVIGATE TO', label: 'Messages', link: '/messages' },
  { id: 'nav-reports', kind: 'nav', category: 'NAVIGATE TO', label: 'Reports', link: '/reports' },
  { id: 'nav-settings', kind: 'nav', category: 'NAVIGATE TO', label: 'Settings', link: '/settings' },
];

function matchesKeywords(text: string, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  const hay = text.toLowerCase();
  return q.split(/\s+/).every((part) => hay.includes(part));
}

function ResultIcon({ icon }: { icon?: PaletteResult['icon'] }) {
  const cls = 'h-4 w-4 shrink-0 text-slate-400';
  switch (icon) {
    case 'zap':
      return <Zap className={cls} />;
    case 'ticket':
      return <Ticket className={cls} />;
    case 'sale':
      return <ShoppingCart className={cls} />;
    case 'user':
      return <User className={cls} />;
    case 'package':
      return <Package className={cls} />;
    case 'dollar':
      return <DollarSign className={cls} />;
    case 'clipboard':
      return <ClipboardList className={cls} />;
    default:
      return <Search className={cls} />;
  }
}

export function CommandPalette({
  open,
  onClose,
  onOpenCash,
}: {
  open: boolean;
  onClose: () => void;
  onOpenCash?: () => void;
}) {
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const [pricePreview, setPricePreview] = useState<RepairServiceSearchHit | null>(null);
  const [recent, setRecent] = useState<PaletteRecentItem[]>([]);
  const debounced = useDebouncedValue(query, 200);
  const q = debounced.trim();
  const searching = q.length >= 1;

  useEffect(() => {
    if (!open) {
      setQuery('');
      setActiveIndex(0);
      setPricePreview(null);
      return;
    }
    setRecent(loadPaletteRecent());
    const t = window.setTimeout(() => inputRef.current?.focus(), 30);
    return () => window.clearTimeout(t);
  }, [open]);

  const staticCommands = useMemo(() => {
    const all = [...QUICK_ACTIONS, ...NAV_ITEMS];
    if (!searching) return all;
    return all.filter((item) =>
      matchesKeywords(`${item.label} ${item.category} ${item.shortcut ?? ''}`, q),
    );
  }, [q, searching]);

  const searchEnabled = open && searching;

  const [ticketsQ, salesQ, customersQ, inventoryQ, pricesQ] = useQueries({
    queries: [
      {
        queryKey: ['palette-tickets', q],
        queryFn: () => listTickets({ search: q, pageSize: 8, excludeSource: 'pos_sale' }),
        enabled: searchEnabled,
      },
      {
        queryKey: ['palette-sales', q],
        queryFn: () => listSales({ search: q, pageSize: 5 }),
        enabled: searchEnabled,
      },
      {
        queryKey: ['palette-customers', q],
        queryFn: () => listCustomers({ q, pageSize: 5 }),
        enabled: searchEnabled,
      },
      {
        queryKey: ['palette-inventory', q],
        queryFn: () => listInventoryItems({ search: q, pageSize: 5, sellableOnly: true }),
        enabled: searchEnabled,
      },
      {
        queryKey: ['palette-prices', q],
        queryFn: () => searchRepairServices(q, 5),
        enabled: searchEnabled && q.length >= 2,
      },
    ],
  });

  // results built below

  const results = useMemo(() => {
    const items: PaletteResult[] = [];

    if (!searching) {
      for (const r of recent) {
        items.push({
          id: `recent-${r.type}-${r.id}`,
          kind: 'recent',
          category: 'RECENT',
          label: r.label,
          sublabel: r.sublabel,
          link: r.link,
          icon:
            r.type === 'ticket'
              ? r.label.startsWith('SALE-')
                ? 'sale'
                : 'ticket'
              : r.type === 'customer'
                ? 'user'
                : r.type === 'inventory'
                  ? 'package'
                  : r.type === 'price'
                    ? 'dollar'
                    : 'clipboard',
        });
      }
      items.push(...QUICK_ACTIONS);
      items.push(...NAV_ITEMS);
      return items;
    }

    if (staticCommands.length > 0) {
      for (const cmd of staticCommands) {
        items.push({ ...cmd, category: cmd.kind === 'nav' ? 'NAVIGATE TO' : 'ACTIONS' });
      }
    }

    const ticketHits = ticketsQ.data?.data ?? [];
    for (const t of ticketHits.filter((row) => !isPosSaleTicket(row))) {
      items.push(paletteTicketResult(t));
    }

    for (const sale of salesQ.data?.data ?? []) {
      items.push(paletteSaleResult(sale));
    }

    for (const c of customersQ.data?.data ?? []) {
      const name = `${c.firstName} ${c.lastName}`.trim();
      items.push({
        id: `customer-${c.id}`,
        kind: 'customer',
        category: 'CUSTOMERS',
        label: `${name}${c.phone ? ` · ${c.phone}` : ''}`,
        sublabel: `${c.totalTickets} tickets · ${c.loyaltyPointsBalance ?? 0} loyalty points`,
        link: `/customers/${c.id}`,
        icon: 'user',
      });
    }

    for (const item of inventoryQ.data?.data ?? []) {
      items.push({
        id: `inv-${item.id}`,
        kind: 'inventory',
        category: 'INVENTORY',
        label: item.name,
        sublabel: `${item.barcodeInternal ?? 'No SKU'} · Qty ${item.qtyOnHand}`,
        link: `/inventory/${item.id}`,
        icon: 'package',
      });
    }

    for (const hit of pricesQ.data ?? []) {
      const price = hit.salePrice ?? hit.price;
      items.push({
        id: `price-${hit.serviceId}-${hit.modelName}`,
        kind: 'price',
        category: 'PRICES',
        label: `${hit.modelName ?? 'Device'} · ${hit.serviceName}`,
        sublabel: [
          formatCurrency(price),
          hit.estimatedMinutes != null ? `Est ${hit.estimatedMinutes}min` : null,
          hit.warrantyDays != null ? `${hit.warrantyDays} day warranty` : null,
        ]
          .filter(Boolean)
          .join(' · '),
        action: 'price_preview',
        priceHit: hit,
        icon: 'dollar',
      });
    }

    return items;
  }, [
    searching,
    recent,
    staticCommands,
    ticketsQ.data,
    salesQ.data,
    customersQ.data,
    inventoryQ.data,
    pricesQ.data,
  ]);

  const flat = results;
  const safeIndex = flat.length === 0 ? 0 : Math.min(activeIndex, flat.length - 1);

  useEffect(() => {
    setActiveIndex(0);
  }, [q, open]);

  function selectResult(item: PaletteResult) {
    if (item.action === 'open_cash' || item.action === 'close_cash') {
      pushPaletteRecent({
        type: 'action',
        id: item.id,
        label: item.label,
        link: '/cash',
      });
      onClose();
      onOpenCash?.();
      return;
    }

    if (item.action === 'price_preview' && item.priceHit) {
      setPricePreview(item.priceHit);
      pushPaletteRecent({
        type: 'price',
        id: `${item.priceHit.serviceId}-${item.priceHit.modelName}`,
        label: item.label,
        sublabel: item.sublabel,
        link: '/settings/repair-services',
      });
      return;
    }

    if (item.link) {
      const recentType: PaletteRecentItem['type'] =
        item.kind === 'ticket' || item.kind === 'sale'
          ? 'ticket'
          : item.kind === 'customer'
            ? 'customer'
            : item.kind === 'inventory'
              ? 'inventory'
              : item.kind === 'nav'
                ? 'nav'
                : 'action';
      pushPaletteRecent({
        type: recentType,
        id: item.id,
        label: item.label,
        sublabel: item.sublabel,
        link: item.link,
      });
      onClose();
      navigate(item.link);
    }
  }

  useEffect(() => {
    if (!open) return;
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        event.preventDefault();
        if (pricePreview) {
          setPricePreview(null);
          return;
        }
        onClose();
        return;
      }
      if (event.key === 'ArrowDown') {
        event.preventDefault();
        setActiveIndex((i) => Math.min(i + 1, Math.max(flat.length - 1, 0)));
      }
      if (event.key === 'ArrowUp') {
        event.preventDefault();
        setActiveIndex((i) => Math.max(i - 1, 0));
      }
      if (event.key === 'Enter' && flat[safeIndex]) {
        event.preventDefault();
        selectResult(flat[safeIndex]!);
      }
    }
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- selectResult uses latest closures via flat/safeIndex
  }, [open, flat, safeIndex, pricePreview, onClose]);

  if (!open) return null;

  const grouped = flat.reduce<Array<{ category: string; items: Array<{ item: PaletteResult; index: number }> }>>(
    (acc, item, index) => {
      const last = acc[acc.length - 1];
      if (last && last.category === item.category) {
        last.items.push({ item, index });
      } else {
        acc.push({ category: item.category, items: [{ item, index }] });
      }
      return acc;
    },
    [],
  );

  const isLoading =
    searching &&
    (ticketsQ.isLoading || customersQ.isLoading || inventoryQ.isLoading || (q.length >= 2 && pricesQ.isLoading));

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-slate-950/50 p-4 pt-[8vh]">
      <div
        className="w-full max-w-2xl overflow-hidden rounded-2xl bg-white shadow-2xl dark:bg-slate-900"
        role="dialog"
        aria-modal="true"
        aria-label="Command palette"
      >
        <div className="flex items-center gap-3 border-b border-slate-200 px-4 py-3 dark:border-slate-700">
          <Search className="h-5 w-5 text-slate-400" />
          <input
            ref={inputRef}
            autoFocus
            className="min-w-0 flex-1 border-0 bg-transparent text-base text-slate-950 outline-none dark:text-white"
            placeholder="Search anything..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          <kbd className="hidden rounded bg-slate-100 px-2 py-1 text-xs text-slate-500 sm:inline dark:bg-slate-800">
            Esc
          </kbd>
          <button
            type="button"
            className="rounded-lg p-2 text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800"
            onClick={onClose}
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {pricePreview ? (
          <div className="space-y-3 p-5">
            <div className="text-xs font-bold uppercase tracking-wide text-slate-500">Price card</div>
            <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 dark:border-slate-700 dark:bg-slate-800">
              <div className="text-lg font-semibold text-slate-950 dark:text-white">
                {pricePreview.modelName} · {pricePreview.serviceName}
              </div>
              <div className="mt-2 text-2xl font-bold text-primary">
                {formatCurrency(pricePreview.salePrice ?? pricePreview.price)}
              </div>
              <div className="mt-2 text-sm text-slate-600 dark:text-slate-300">
                {[
                  pricePreview.estimatedMinutes != null ? `Est ${pricePreview.estimatedMinutes} min` : null,
                  pricePreview.warrantyDays != null ? `${pricePreview.warrantyDays}-day warranty` : null,
                ]
                  .filter(Boolean)
                  .join(' · ')}
              </div>
            </div>
            <button
              type="button"
              className="rounded-xl border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
              onClick={() => setPricePreview(null)}
            >
              Back to results
            </button>
          </div>
        ) : (
          <div className="max-h-[62vh] overflow-y-auto p-2">
            {isLoading ? (
              <p className="p-4 text-sm text-slate-500">Searching…</p>
            ) : flat.length === 0 ? (
              <p className="p-4 text-sm text-slate-500">No results.</p>
            ) : (
              grouped.map((group) => (
                <div key={group.category} className="mb-2">
                  <div className="px-3 py-2 text-[11px] font-bold uppercase tracking-wider text-slate-400">
                    {group.category}
                  </div>
                  {group.items.map(({ item, index }) => (
                    <button
                      key={item.id}
                      type="button"
                      onMouseEnter={() => setActiveIndex(index)}
                      onClick={() => selectResult(item)}
                      className={cn(
                        'flex w-full items-start gap-3 rounded-xl px-3 py-2.5 text-left transition',
                        index === safeIndex
                          ? 'bg-primary/10 text-slate-950 dark:bg-primary/20 dark:text-white'
                          : 'hover:bg-slate-50 dark:hover:bg-slate-800',
                      )}
                    >
                      <ResultIcon icon={item.icon} />
                      <div className="min-w-0 flex-1">
                        <div className="truncate text-sm font-medium text-slate-900 dark:text-slate-100">
                          {item.label}
                        </div>
                        {item.sublabel ? (
                          <div className="truncate text-xs text-slate-500">{item.sublabel}</div>
                        ) : null}
                      </div>
                      {item.shortcut ? (
                        <span className="shrink-0 text-xs text-slate-400">{item.shortcut}</span>
                      ) : null}
                    </button>
                  ))}
                </div>
              ))
            )}
          </div>
        )}

        {!searching && !pricePreview ? (
          <div className="space-y-1 border-t border-slate-100 px-4 py-3 text-xs text-slate-400 dark:border-slate-800">
            <p>Tip: Try searching for ticket numbers, customer names, or repair prices</p>
            <p className="flex flex-wrap gap-x-3 gap-y-1">
              <span>
                <kbd className="rounded bg-slate-100 px-1 dark:bg-slate-800">⌘K</kbd> Open
              </span>
              <span>
                <kbd className="rounded bg-slate-100 px-1 dark:bg-slate-800">⌘N</kbd> Quick ticket
              </span>
              <span>
                <kbd className="rounded bg-slate-100 px-1 dark:bg-slate-800">⌘P</kbd> POS
              </span>
              <span>
                <kbd className="rounded bg-slate-100 px-1 dark:bg-slate-800">↑↓</kbd> Navigate
              </span>
              <span>
                <kbd className="rounded bg-slate-100 px-1 dark:bg-slate-800">Enter</kbd> Select
              </span>
              <span>
                <kbd className="rounded bg-slate-100 px-1 dark:bg-slate-800">Esc</kbd> Close
              </span>
            </p>
          </div>
        ) : null}
      </div>
    </div>
  );
}

/** @deprecated Use CommandPalette — kept for import compatibility */
export { CommandPalette as QuoteSearchModal };
